/**
 * pipeline-data — pure derivations for the single-pane Pipeline view (Wave 2c).
 *
 * The Pipeline view interleaves trials from every run (the `/host-lens` payload)
 * and rolls runs up (the `/runs` payload). These helpers are the DOM-free,
 * React-free seam between those two snapshots and the presentational components:
 * recency ordering for the trial stream, the aggregate throughput number the
 * PipelineBar reads, and the terminal/active split the run-fidelity scope and the
 * History view filter on. Every rule is a pure function so it is unit-testable in
 * isolation and the view only renders what these return.
 */

import type { BoardFilter } from './board-concurrency'
import type { TrialLite } from './board-types'
import type { RunOverviewEntry, RunStatusCounts } from './types'

/** The zero counts the PipelineBar reads before the first host-lens poll lands. */
export const EMPTY_STATUS_COUNTS: RunStatusCounts = {
  queued: 0,
  leased: 0,
  running: 0,
  interrupted: 0,
  retryable: 0,
  succeeded: 0,
  failed: 0,
  cancelled: 0,
}

/**
 * The recency key for a trial: its most recent lifecycle timestamp. A finished
 * trial sorts by completion, a running one by start, a leased one by lease, and a
 * still-queued one by creation — so "newest activity first" stays honest across
 * every lane.
 */
export function trialActivityAt(trial: TrialLite): number {
  return trial.completedAt ?? trial.startedAt ?? trial.leasedAt ?? trial.createdAt
}

/**
 * Order trials newest-activity-first for the ActivityStream. The host-lens scan
 * returns trials grouped by `run, arm, scenario, index` (read-model
 * `listAllJobs`), NOT by recency, so the stream must sort them here. Pure —
 * returns a new array, never mutates the input.
 */
export function sortTrialsByRecency(trials: readonly TrialLite[]): TrialLite[] {
  return [...trials].sort((a, b) => trialActivityAt(b) - trialActivityAt(a))
}

/**
 * The aggregate pipeline throughput the PipelineBar prints: the trailing-5-minute
 * completions-per-minute summed across every run in the overview. The host-lens
 * payload carries no throughput of its own, so this is derived from the `/runs`
 * rollup (each run's `throughputPerMin`).
 */
export function pipelineThroughput(runs: readonly RunOverviewEntry[]): number {
  return runs.reduce((sum, run) => sum + run.throughputPerMin, 0)
}

/**
 * True when a run still has trials in a non-terminal lane (queued, leased,
 * running, interrupted, retryable). Mirrors the read model's own
 * `hasOutstandingWork` so the client's active/finished split matches the server's
 * recency-sort + finished-cap exactly. Used by the run-fidelity scope and the
 * History view's finished filter.
 */
export function hasOutstandingWork(counts: RunStatusCounts): boolean {
  return counts.queued + counts.leased + counts.running + counts.interrupted + counts.retryable > 0
}

/** The three kanban lanes for run-level state. */
export type RunLane = 'running' | 'stuck' | 'done'

/**
 * Maps a run to its kanban lane.
 *   - Stuck   → stalled flag is set
 *   - Done    → no outstanding work remains
 *   - Running → everything else
 */
export function runLaneForState(run: RunOverviewEntry): RunLane {
  if (run.stalled) return 'stuck'
  if (!hasOutstandingWork(run.counts)) return 'done'
  return 'running'
}

/**
 * Filter runs by free-text query: matches against runId and name
 * (case-insensitive, substring). Lane chips are trial-specific and hidden
 * at run fidelity, so only the query field is applied here.
 * Returns a new array — never mutates the input.
 */
export function filterRuns(runs: readonly RunOverviewEntry[], filter: BoardFilter): RunOverviewEntry[] {
  const q = filter.query.trim().toLowerCase()
  if (q === '') return [...runs]
  return runs.filter((run) => run.runId.toLowerCase().includes(q) || run.name.toLowerCase().includes(q))
}

/**
 * The stalest of two last-successful-contact timestamps. The topbar freshness pill
 * must reflect the OLDEST data the surface depends on — the Pipeline body is backed
 * by `/host-lens` while the run rollup is backed by `/runs`, so reporting only one
 * feed's freshness can show "connected" while the other is stale. A null means a
 * feed has never succeeded; the older of two real timestamps wins, and a single
 * real timestamp is used when the other feed has not landed yet.
 */
export function olderSuccessAt(a: number | null, b: number | null): number | null {
  if (a === null) return b
  if (b === null) return a
  return Math.min(a, b)
}
